import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  ADS_STEUERUNG_KEY,
  AdsSteuerungError,
  adsErrorCode,
  adsQueryRetry,
  isAdsSteuerungError,
} from '@/hooks/useAdsSteuerung';

/**
 * Lese- und Schreibpfad des Abschnitts "Kampagnenstruktur" auf
 * `/internal/ads-steuerung` (Ads-Ausbau 0.7, Task 6).
 *
 * Gegenstueck: die Edge Function `ads-structure-run`
 * (`supabase/functions/ads-structure-run/handlers.ts`). Vier Aktionen:
 * `structure_overview`, `set_budget`, `set_troas`, `campaign_pause`.
 * Spec `docs/superpowers/specs/2026-08-25-ads-struktur-steuerpult-design.md`
 * (Abschnitte 10 bis 12) und die Delta-Spec
 * `docs/superpowers/specs/2026-09-25-ads-struktur-steuerpult-delta.md`
 * (Δ1 bis Δ13), beide im Root-Repo. Wo sie sich widersprechen, gilt die
 * Delta-Spec -- sie ist der Stand des tatsaechlich gebauten Handlers.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WARUM HIER KEIN EINZIGER DIREKTER TABELLENZUGRIFF STEHT
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * `ads-structure-run` prueft Besitz selbst ueber die Rollout-Kette
 * (`callerOperatesRollout`) und liest alle Kampagnendaten live von Google --
 * es gibt keine Tabelle, aus der diese Zahlen direkt kaemen. Ein
 * `supabase.from(...)` waere hier also nicht nur Stilbruch, es gaebe die
 * Daten gar nicht her. Deshalb genau eine Zustellstelle:
 * `invokeAdsStructureRun`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ZWEI PRUEFUNGEN, NICHT EINE
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * `structureWriteBlock` unten ist die ZWEITE Instanz der Sperr-Verrechnung,
 * nicht die einzige. Die Function rechnet dieselben Regeln serverseitig
 * (`_shared/ads-structure-eligibility.ts:decideStructureWriteEligibility`)
 * und lehnt jeden Schreibzugriff unabhaengig von dieser Funktion hier ab. Ein
 * Knopf, der erst beim Klick "nein" sagt, hat den Nutzer bereits in die
 * Handlung gefuehrt -- die Regel gehoert in den Zustand des Knopfes. Und ein
 * Server, der sich auf die ausgegraute Schaltflaeche verlaesst, ist ueber
 * `curl` offen. Keine der beiden Seiten darf sich auf die andere verlassen.
 *
 * `structureWriteBlock` parrotiert die Uebersicht dabei nicht bloss: die
 * Function liefert je Kampagne bereits ein vorgerechnetes `write_block`
 * (Stufe "Anzeige", ohne Schrittweite und ohne Begruendungspflicht), diese
 * Funktion prueft zusaetzlich, unabhaengig von der Antwort, ob ein
 * Cooldown-Zeitstempel in der Zukunft liegt -- die eine Groesse, die sich aus
 * den rohen Feldern der Uebersicht selbst nachrechnen laesst, ohne dass die
 * Function irgendetwas dafuer extra melden muss.
 */

// ═════════════════════════════════════════════════════════════════════════════
// Vertrag (Handler-Antwortform, Kurzform in Spec Paragraph 12, Feldnamen aus
// supabase/functions/ads-structure-run/handlers.ts)
// ═════════════════════════════════════════════════════════════════════════════

/** Nur die drei Hebel, die die App bedient (Delta Δ6: kein `set_cpc_ceiling` in v1). */
export type StructureLever = 'budget' | 'troas' | 'pause';

export type StructureGuardrailToday = 'breach' | 'clear' | 'unknown';

export interface StructureSpendView {
  cost_micros: number;
  conversions: number;
  conversions_value: number;
}

export interface StructureBudgetView {
  resource_name: string | null;
  /** int64 als String. */
  amount_micros: string | null;
  explicitly_shared: boolean | null;
}

export interface StructureBiddingView {
  strategy_type: string | null;
  portfolio_strategy: string | null;
  target_roas: number | null;
  /** int64 als String. */
  cpc_ceiling_micros: string | null;
  system_status: string | null;
}

export interface StructureCooldownsView {
  unknown: boolean;
  budget_last_change_at: string | null;
  budget_next_allowed_at: string | null;
  troas_last_change_at: string | null;
  troas_next_allowed_at: string | null;
}

export interface StructureTroasGateView {
  conversions_30d: number | null;
  campaign_age_days: number | null;
  conversions_ok: boolean;
  age_ok: boolean;
}

export interface StructureWriteBlockView {
  budget: string | null;
  troas: string | null;
  pause: string | null;
}

export interface StructureCampaignView {
  resource_name: string;
  id: string | null;
  name: string | null;
  status: string | null;
  channel_type: string | null;
  /** Steht im Register `ads_structure_campaigns` (retired_at IS NULL) -- Delta Δ1. */
  is_structure: boolean;
  role: string | null;
  start_date: string | null;
  budget: StructureBudgetView;
  bidding: StructureBiddingView;
  spend_7d: StructureSpendView;
  spend_30d: StructureSpendView;
  measured_roas_30d: number | null;
  campaign_age_days: number | null;
  cooldowns: StructureCooldownsView;
  evaluation_marker: string | null;
  evaluation_window: boolean;
  drift: { budget: boolean; troas: boolean };
  troas_first_set_gate: StructureTroasGateView;
  troas_stable: boolean;
  /**
   * Vorgerechneter Sperrgrund je Hebel, Stufe "Anzeige". Traegt bewusst NIE
   * `MEASUREMENT_REASON_REQUIRED` (die Function rechnet die Anzeige mit einer
   * Platzhalter-Begruendung, damit das Begruendungsfeld nicht als serverseitig
   * "schon gesperrt" erscheint, bevor jemand die Zahlen gesehen hat) und NIE
   * `STEP_TOO_LARGE` (die Anzeige kennt keinen Zielwert, gegen den sich eine
   * Schrittweite pruefen liesse).
   */
  write_block: StructureWriteBlockView;
}

export interface StructureAccountView {
  ads_customer_id: string;
  label: string | null;
  role: string | null;
  write_enabled: boolean;
  /** int64 als String, `null` = kein Strukturbudget hinterlegt. */
  structure_budget_micros: string | null;
  currency: string | null;
  mcc_reachable: boolean;
}

export interface StructureOverview {
  account: StructureAccountView;
  /** Kontoebene: `WRITE_DISABLED` | `STRUCTURE_MORATORIUM` | `GUARDRAIL_BREACH_TODAY` | `null`. */
  write_blocked_reason: string | null;
  measurement_window_active: boolean;
  moratorium_active: boolean;
  guardrail_today: StructureGuardrailToday;
  notes: string[];
  campaigns: StructureCampaignView[];
}

/** `preview.lever` bestimmt, welche `current_*`/`new_*`-Schluessel gefuellt sind. */
export interface StructurePreviewBody {
  campaign: string;
  name: string | null;
  role: string | null;
  lever: StructureLever;
  current_budget_micros?: string | null;
  new_budget_micros?: string | null;
  current_target_roas?: number | null;
  new_target_roas?: number | null;
  current_status?: string | null;
  new_status?: string | null;
  /** Nur `troas`: der Ist-ROAS-Anker, bei der Erstsetzung UND danach. */
  anchor_roas?: number | null;
  /** Nur `troas`: war vor diesem Lauf noch kein Ziel gesetzt? */
  first_set?: boolean;
  change_abs: number | string | null;
  change_pct: number | null;
  last_change_at: string | null;
  next_allowed_at: string | null;
  next_allowed_after_write: string | null;
  warnings: string[];
  /** true = Messfenster aktiv, der reale Lauf braucht `reason`. */
  reason_required: boolean;
  validate_response: unknown;
}

export interface StructurePreview {
  dry_run: true;
  preview: StructurePreviewBody;
}

export interface StructureBudgetSum {
  shifted: boolean;
  after: string | null;
  error: string | null;
}

export interface StructureWriteResult {
  ok: true;
  dry_run: false;
  campaign: string;
  lever: StructureLever;
  old: string | number | null;
  new: string | number;
  change_pct: number | null;
  verify_ok: true;
  logged: boolean;
  log_error: string | null;
  budget_sum: StructureBudgetSum | null;
  warnings: string[];
  /**
   * Der reale Mutate warf (Timeout/5xx bei Google), aber das anschliessende
   * Read-back bestaetigt trotzdem den neuen Wert -- der Handler behandelt das
   * als Erfolg (der Schreibzugriff ist erwiesenermassen angekommen), meldet
   * den Fehler aber mit (`handlers.ts:900,971`: `mutate_warning` steht nur im
   * Body, wenn `mutateError !== null`). `null` heisst: kein Mutate-Fehler.
   */
  mutate_warning: string | null;
}

/** Antwort auf `set_budget`/`set_troas`/`campaign_pause`, je nach `dry_run`. */
export type StructureWriteResponse = StructurePreview | StructureWriteResult;

/** Liest den aktuellen Wert eines Vorschau-Ergebnisses ueber alle drei Hebel. */
export function previewCurrentValue(preview: StructurePreviewBody): string | number | null {
  if (preview.lever === 'budget') return preview.current_budget_micros ?? null;
  if (preview.lever === 'troas') return preview.current_target_roas ?? null;
  return preview.current_status ?? null;
}

/** Liest den vorgeschlagenen neuen Wert eines Vorschau-Ergebnisses. */
export function previewNewValue(preview: StructurePreviewBody): string | number | null {
  if (preview.lever === 'budget') return preview.new_budget_micros ?? null;
  if (preview.lever === 'troas') return preview.new_target_roas ?? null;
  return preview.new_status ?? null;
}

// ═════════════════════════════════════════════════════════════════════════════
// Zustellstelle (Muster `invokeAdsTierRun`, `useAdsSteuerung.ts:393-403`)
// ═════════════════════════════════════════════════════════════════════════════

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Codefeld eines Fehler-Bodys: Vertrag schreibt `error`. */
function codeFromBody(body: unknown): string | null {
  if (!isRecord(body)) return null;
  if (typeof body.error === 'string' && body.error.length > 0) return body.error;
  if (typeof body.code === 'string' && body.code.length > 0) return body.code;
  return null;
}

async function throwFromInvokeError(error: unknown): Promise<never> {
  const context = (error as { context?: Response } | null)?.context;
  let body: unknown = null;

  if (context && typeof context.json === 'function' && !context.bodyUsed) {
    try {
      // Klonen, damit ein zweiter Leser den Body noch bekommt.
      const source = typeof context.clone === 'function' ? context.clone() : context;
      body = await source.json();
    } catch {
      body = null;
    }
  }

  const code = codeFromBody(body);
  if (code) throw new AdsSteuerungError(code, context?.status ?? null);
  throw error instanceof Error ? error : new Error(String(error));
}

/** Die einzige Zustellstelle des Abschnitts. */
async function invokeAdsStructureRun<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('ads-structure-run', { body });
  if (error) await throwFromInvokeError(error);

  // Auch ein 200 kann einen Fehler tragen -- Muster `invokeAdsTierRun`.
  const code = codeFromBody(data);
  if (code) throw new AdsSteuerungError(code, 200);
  if (data === null || data === undefined) throw new AdsSteuerungError('EMPTY_RESPONSE', 200);

  return data as T;
}

// ═════════════════════════════════════════════════════════════════════════════
// Normalisierung
// ═════════════════════════════════════════════════════════════════════════════
//
// Diese Function liefert Zahlen bereits als JS-Zahlen (kein PostgREST-Layer
// dazwischen), NUR `structure_budget_micros` und alle int64-Felder
// (`amount_micros`, `cpc_ceiling_micros`) sind bewusst als String unterwegs
// (int64 verliert Praezision als JS-Zahl). Trotzdem defensiv normalisiert,
// dieselbe Haltung wie `num`/`nullableNum` in `useAdsSteuerung.ts`: eine
// Antwort, die die Form nicht traegt, soll nicht crashen, sondern einen
// lesbaren Leerwert liefern.
//
// M5 (Task-Review, bewusst NICHT in dieser Welle geloest): `str`/`num`/
// `nullableNum`/`bool` unten sind wortgleiche Kopien der gleichnamigen
// privaten Helfer in `useAdsSteuerung.ts` (dort nicht exportiert). Ein
// Zusammenzug in ein geteiltes Normalisierungs-Modul ist ein eigener
// Vorgang -- diese Welle baut nur den Struktur-Abschnitt, kein Refactor von
// `useAdsSteuerung.ts`.

function str(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function num(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function nullableNum(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeSpend(value: unknown): StructureSpendView {
  const row = isRecord(value) ? value : {};
  return {
    cost_micros: num(row.cost_micros),
    conversions: num(row.conversions),
    conversions_value: num(row.conversions_value),
  };
}

function normalizeCampaign(value: unknown): StructureCampaignView | null {
  if (!isRecord(value)) return null;
  const resourceName = str(value.resource_name);
  if (!resourceName) return null;
  const budgetRow = isRecord(value.budget) ? value.budget : {};
  const biddingRow = isRecord(value.bidding) ? value.bidding : {};
  const cooldownsRow = isRecord(value.cooldowns) ? value.cooldowns : {};
  const gateRow = isRecord(value.troas_first_set_gate) ? value.troas_first_set_gate : {};
  const driftRow = isRecord(value.drift) ? value.drift : {};
  const writeBlockRow = isRecord(value.write_block) ? value.write_block : {};

  return {
    resource_name: resourceName,
    id: str(value.id),
    name: str(value.name),
    status: str(value.status),
    channel_type: str(value.channel_type),
    is_structure: bool(value.is_structure),
    role: str(value.role),
    start_date: str(value.start_date),
    budget: {
      resource_name: str(budgetRow.resource_name),
      amount_micros: str(budgetRow.amount_micros),
      explicitly_shared: typeof budgetRow.explicitly_shared === 'boolean' ? budgetRow.explicitly_shared : null,
    },
    bidding: {
      strategy_type: str(biddingRow.strategy_type),
      portfolio_strategy: str(biddingRow.portfolio_strategy),
      target_roas: nullableNum(biddingRow.target_roas),
      cpc_ceiling_micros: str(biddingRow.cpc_ceiling_micros),
      system_status: str(biddingRow.system_status),
    },
    spend_7d: normalizeSpend(value.spend_7d),
    spend_30d: normalizeSpend(value.spend_30d),
    measured_roas_30d: nullableNum(value.measured_roas_30d),
    campaign_age_days: nullableNum(value.campaign_age_days),
    cooldowns: {
      unknown: bool(cooldownsRow.unknown),
      budget_last_change_at: str(cooldownsRow.budget_last_change_at),
      budget_next_allowed_at: str(cooldownsRow.budget_next_allowed_at),
      troas_last_change_at: str(cooldownsRow.troas_last_change_at),
      troas_next_allowed_at: str(cooldownsRow.troas_next_allowed_at),
    },
    evaluation_marker: str(value.evaluation_marker),
    evaluation_window: bool(value.evaluation_window),
    drift: { budget: bool(driftRow.budget), troas: bool(driftRow.troas) },
    troas_first_set_gate: {
      conversions_30d: nullableNum(gateRow.conversions_30d),
      campaign_age_days: nullableNum(gateRow.campaign_age_days),
      conversions_ok: bool(gateRow.conversions_ok),
      age_ok: bool(gateRow.age_ok),
    },
    troas_stable: bool(value.troas_stable),
    write_block: {
      budget: str(writeBlockRow.budget),
      troas: str(writeBlockRow.troas),
      pause: str(writeBlockRow.pause),
    },
  };
}

export function normalizeStructureOverview(raw: unknown): StructureOverview {
  const row = isRecord(raw) ? raw : {};
  const accountRow = isRecord(row.account) ? row.account : {};
  const campaignsRaw = Array.isArray(row.campaigns) ? row.campaigns : [];
  const guardrailToday = row.guardrail_today;

  return {
    account: {
      ads_customer_id: str(accountRow.ads_customer_id) ?? '',
      label: str(accountRow.label),
      role: str(accountRow.role),
      write_enabled: bool(accountRow.write_enabled),
      structure_budget_micros: str(accountRow.structure_budget_micros),
      currency: str(accountRow.currency),
      mcc_reachable: bool(accountRow.mcc_reachable),
    },
    write_blocked_reason: str(row.write_blocked_reason),
    measurement_window_active: bool(row.measurement_window_active),
    moratorium_active: bool(row.moratorium_active),
    guardrail_today: guardrailToday === 'breach' || guardrailToday === 'clear' ? guardrailToday : 'unknown',
    notes: Array.isArray(row.notes) ? row.notes.filter((n): n is string => typeof n === 'string') : [],
    campaigns: campaignsRaw
      .map((c) => normalizeCampaign(c))
      .filter((c): c is StructureCampaignView => c !== null),
  };
}

function normalizePreviewBody(raw: unknown): StructurePreviewBody | null {
  if (!isRecord(raw)) return null;
  const campaign = str(raw.campaign);
  const lever = raw.lever;
  if (!campaign || (lever !== 'budget' && lever !== 'troas' && lever !== 'pause')) return null;
  return {
    campaign,
    name: str(raw.name),
    role: str(raw.role),
    lever,
    current_budget_micros: 'current_budget_micros' in raw ? str(raw.current_budget_micros) : undefined,
    new_budget_micros: 'new_budget_micros' in raw ? str(raw.new_budget_micros) : undefined,
    current_target_roas: 'current_target_roas' in raw ? nullableNum(raw.current_target_roas) : undefined,
    new_target_roas: 'new_target_roas' in raw ? nullableNum(raw.new_target_roas) : undefined,
    current_status: 'current_status' in raw ? str(raw.current_status) : undefined,
    new_status: 'new_status' in raw ? str(raw.new_status) : undefined,
    anchor_roas: 'anchor_roas' in raw ? nullableNum(raw.anchor_roas) : undefined,
    first_set: typeof raw.first_set === 'boolean' ? raw.first_set : undefined,
    change_abs: typeof raw.change_abs === 'string' || typeof raw.change_abs === 'number' ? raw.change_abs : null,
    change_pct: nullableNum(raw.change_pct),
    last_change_at: str(raw.last_change_at),
    next_allowed_at: str(raw.next_allowed_at),
    next_allowed_after_write: str(raw.next_allowed_after_write),
    warnings: Array.isArray(raw.warnings) ? raw.warnings.filter((w): w is string => typeof w === 'string') : [],
    reason_required: bool(raw.reason_required),
    validate_response: raw.validate_response ?? null,
  };
}

/**
 * Exportiert fuer den Wächter-Test I3f: pinnt die Form der realen
 * Antwort (inkl. `mutate_warning`) unabhaengig von den Hooks.
 */
export function normalizeWriteResponse(raw: unknown): StructureWriteResponse {
  if (!isRecord(raw)) throw new AdsSteuerungError('EMPTY_RESPONSE', 200);

  if (raw.dry_run === true) {
    const preview = normalizePreviewBody(raw.preview);
    if (!preview) throw new AdsSteuerungError('EMPTY_RESPONSE', 200);
    return { dry_run: true, preview };
  }

  if (raw.dry_run === false) {
    const lever = raw.lever;
    if (lever !== 'budget' && lever !== 'troas' && lever !== 'pause') {
      throw new AdsSteuerungError('EMPTY_RESPONSE', 200);
    }
    const budgetSumRow = isRecord(raw.budget_sum) ? raw.budget_sum : null;
    return {
      ok: true,
      dry_run: false,
      campaign: str(raw.campaign) ?? '',
      lever,
      old: typeof raw.old === 'string' || typeof raw.old === 'number' ? raw.old : null,
      new: typeof raw.new === 'string' || typeof raw.new === 'number' ? raw.new : '',
      change_pct: nullableNum(raw.change_pct),
      verify_ok: true,
      logged: bool(raw.logged),
      log_error: str(raw.log_error),
      budget_sum: budgetSumRow
        ? { shifted: bool(budgetSumRow.shifted), after: str(budgetSumRow.after), error: str(budgetSumRow.error) }
        : null,
      warnings: Array.isArray(raw.warnings) ? raw.warnings.filter((w): w is string => typeof w === 'string') : [],
      mutate_warning: typeof raw.mutate_warning === 'string' && raw.mutate_warning.length > 0 ? raw.mutate_warning : null,
    };
  }

  throw new AdsSteuerungError('EMPTY_RESPONSE', 200);
}

// ═════════════════════════════════════════════════════════════════════════════
// Zweite Instanz der Knopf-Zustaende
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Sperrgrund fuer einen Hebel an einer Kampagne, oder `null` wenn (nach dem
 * Stand der Uebersicht) nichts dagegen spricht.
 *
 * Reihenfolge (Delta-Spec Abschnitt 2, so weit sie sich aus der Uebersicht
 * allein rekonstruieren laesst):
 *   1. Kontosperre (`overview.write_blocked_reason`) gewinnt vor jeder
 *      Kampagnen-Ueberlegung -- golfimport soll nie einen kampagnenbezogenen
 *      Grund zeigen, sondern immer WRITE_DISABLED.
 *   2. Das MCC ist nicht erreichbar (`account.mcc_reachable === false`) --
 *      account-weit wie Schritt 1, unabhaengig von der einzelnen Kampagne.
 *      Der Handler prueft dasselbe erst spaet, unmittelbar vor `validateOnly`
 *      (`handlers.ts:809-812`), aber es ist eine Konto-Eigenschaft und gehoert
 *      hier vor die Kampagnen-Ueberlegung.
 *   3. Alt-Kampagnen (nicht im Register) sind fuer jeden Hebel gesperrt.
 *   4. Der vorgerechnete Sperrgrund der Function fuer diesen Hebel
 *      (`campaign.write_block[lever]`) -- deckt SHARED_BUDGET, COOLDOWN_ACTIVE,
 *      die beiden TROAS_GATE-*-Codes, TROAS_NOT_VALUE_BIDDING,
 *      PORTFOLIO_STRATEGY und ALREADY_PAUSED ab.
 *   5. Als zweite, unabhaengige Pruefung: liegt der Cooldown-Zeitstempel
 *      dieses Hebels in der Zukunft? Normalerweise deckungsgleich mit Schritt
 *      4, aber eine Zeile, die diese Funktion selbst nachrechnet statt nur
 *      durchzureichen.
 *
 * `MEASUREMENT_REASON_REQUIRED` taucht hier bewusst NICHT auf: die Function
 * rechnet `write_block` mit einer Platzhalter-Begruendung (Anzeige-Stufe),
 * die Begruendungspflicht ist Sache des Bestaetigungsdialogs, nicht des
 * Knopf-Zustands -- sonst waere der Budget-Knopf im Messfenster IMMER
 * gesperrt, bevor jemand ueberhaupt eine Begruendung eingeben konnte.
 */
export function structureWriteBlock(
  overview: StructureOverview | null,
  campaign: StructureCampaignView | null,
  lever: StructureLever,
): string | null {
  if (!overview) return null;
  if (overview.write_blocked_reason) return overview.write_blocked_reason;
  if (!overview.account.mcc_reachable) return 'NOT_MCC_REACHABLE';
  if (!campaign) return null;
  if (!campaign.is_structure) return 'NOT_STRUCTURE_CAMPAIGN';

  const serverBlock = campaign.write_block[lever];
  if (serverBlock) return serverBlock;

  const cooldownAt = lever === 'budget'
    ? campaign.cooldowns.budget_next_allowed_at
    : lever === 'troas'
      ? campaign.cooldowns.troas_next_allowed_at
      // Die Pause hat keinen Cooldown (Spec Paragraph 5.4, LEVER_COOLDOWN_DAYS.pause = null).
      : null;
  if (cooldownAt) {
    const ms = Date.parse(cooldownAt);
    if (Number.isFinite(ms) && ms > Date.now()) return 'COOLDOWN_ACTIVE';
  }

  return null;
}

// ═════════════════════════════════════════════════════════════════════════════
// STRUCTURE_ERROR_TEXT -- Delta-Spec Abschnitt "Delta 13 Fehlercodes v1"
// ═════════════════════════════════════════════════════════════════════════════
//
// Pflicht-Hinweis 1 (Spec Paragraph 6.1, wortgleich in
// `_shared/ads-structure-eligibility.ts`): Budget- und Zielaenderungen setzen
// die Lernphase NICHT zurueck, Google sagt ausdruecklich das Gegenteil. Kein
// Text hier darf einen Lernphasen-Reset behaupten -- COOLDOWN_ACTIVE
// begruendet mit Volatilitaet (die Leitplanken richten sich gegen
// Spend-Schwankungen), nicht mit einer zurueckgesetzten Lernphase.

export const STRUCTURE_ERROR_TEXT: Record<string, string> = {
  UNAUTHORIZED: 'Nicht angemeldet, oder die Sitzung ist abgelaufen.',
  NOT_A_ROLLOUT_OPERATOR:
    'Dieser Zugang bedient den Rollout nicht. Die Fläche steht nur Nutzern offen, denen '
    + 'mindestens eines der eingeschriebenen Ads-Konten gehört.',
  ADS_CUSTOMER_ID_REQUIRED: 'Es wurde kein Konto mitgeschickt.',
  NOT_ENROLLED: 'Dieses Ads-Konto ist nicht in den Rollout eingeschrieben.',
  ACCOUNT_NOT_AVAILABLE: 'Dieses Ads-Konto gehört nicht zu diesem Zugang.',
  CAMPAIGN_REQUIRED: 'Es wurde keine gültige Kampagne mitgeschickt.',
  CAMPAIGN_NOT_FOUND: 'Die Kampagne wurde bei Google nicht gefunden. Vielleicht wurde sie entfernt.',
  NOT_STRUCTURE_CAMPAIGN:
    'Diese Kampagne steht nicht im Struktur-Register. Alt-Kampagnen sind unantastbar.',
  NOT_MCC_REACHABLE:
    'Dieses Konto hängt nicht am OMA-MCC. Ohne das erreichbare MCC kann hier nicht geschrieben werden.',
  WRITE_DISABLED:
    'Schreiben ist für dieses Konto ausgeschaltet (write_enabled = false). Negativkontrolle: '
    + 'Zahlen ja, Eingriffe nein.',
  STRUCTURE_MORATORIUM:
    'Struktur-Moratorium 01.09. bis 13.09.: direkt nach dem Cutover bleiben Strukturänderungen '
    + 'gesperrt, damit sich die Kampagnen einpendeln können.',
  GUARDRAIL_BREACH_TODAY:
    'Mindestens einer der vier Messplan-Guardrails (G-A bis G-D) steht heute auf Verstoß. '
    + 'Solange das so ist, bleibt das Konto für Struktureingriffe gesperrt.',
  MEASUREMENT_REASON_REQUIRED:
    'Im Messfenster (22.09. bis 19.10.) braucht jede Änderung eine Begründung, damit sie in '
    + 'der Auswertung als Einschränkung sichtbar ist.',
  SHARED_BUDGET:
    'Dieses Budget ist mit anderen Kampagnen geteilt. Eine Änderung hier träfe auch die '
    + 'anderen, deshalb bleibt der Knopf gesperrt.',
  COOLDOWN_ACTIVE:
    'Der Mindestabstand zwischen zwei Änderungen dieses Hebels ist noch nicht erreicht. Google '
    + 'braucht Zeit, um eine Änderung einzupendeln, bevor die nächste kommt, das Ziel ist '
    + 'Stabilität gegen Ausreißer.',
  STEP_TOO_LARGE:
    'Der Schritt ist größer als 20 Prozent. Ein größerer Sprung destabilisiert das Gebot, '
    + 'bitte in kleineren Schritten ändern. Bei einer Erstsetzung des Wertziels ohne messbaren '
    + 'Ist-ROAS (keine Kosten in 30 Tagen) gibt es keinen Anker, gegen den sich der Schritt '
    + 'messen ließe; dann bleibt die Erstsetzung gesperrt, bis die Kampagne Kosten hat.',
  TROAS_GATE_CONVERSIONS:
    'Weniger als 15 Conversions in 30 Tagen. Für eine erste Zielsetzung braucht Google eine '
    + 'belastbare Datengrundlage.',
  TROAS_GATE_AGE:
    'Die Kampagne ist jünger als 28 Tage. Ein Wertziel braucht einen verlässlichen Ist-ROAS '
    + 'als Anker, und der stellt sich erst mit der Zeit ein.',
  TROAS_NOT_VALUE_BIDDING:
    'Diese Kampagne fährt keine Wert-Gebotsstrategie. Ein Wertziel liefe bei Google auf eine '
    + 'Kampagne, die es nicht kennt.',
  PORTFOLIO_STRATEGY:
    'Diese Kampagne hängt an einer Portfolio-Strategie. Ein Wertziel gehört auf die Strategie, '
    + 'nicht auf die einzelne Kampagne.',
  ALREADY_PAUSED: 'Die Kampagne steht bereits auf Pausiert.',
  STALE_PREVIEW: 'Der Wert hat sich seit der Vorschau geändert. Bitte neu ansehen.',
  VALUE_REQUIRED: 'Kein gültiger Wert übergeben, oder er entspricht bereits dem aktuellen Stand.',
  VALIDATE_FAILED: 'Google hat die Vorschau-Prüfung abgelehnt. Es wurde nichts geschrieben.',
  WRITE_GUARD_REFUSED:
    'Die Datenbank hat den Schreibzugriff unmittelbar vor dem Google-Aufruf abgelehnt. Es wurde '
    + 'nichts geschrieben.',
  MUTATE_FAILED: 'Der Schreibzugriff bei Google ist gescheitert.',
  VERIFY_FAILED:
    'Der Schreibzugriff ging an Google, aber das Nachlesen zeigt nicht den erwarteten Wert. '
    + 'Bitte die Kampagne von Hand prüfen.',
  TOKEN_UNAVAILABLE: 'Das Google-Token ist nicht verfügbar oder abgelaufen.',
  ADS_API_ERROR: 'Die Google Ads API hat mit einem Fehler geantwortet.',
  DATABASE_ERROR: 'Die Datenbank hat nicht geantwortet. Bitte erneut versuchen.',
  UNKNOWN_ACTION: 'Unbekannte Aktion. Das ist ein Fehler der Oberfläche, nicht deiner.',
  EMPTY_RESPONSE: 'Die Function hat nichts zurückgegeben.',
  // Nicht Teil der Delta-13-Liste, aber ein echter Code des Handlers
  // (`loadTokenContext`): fehlt das Developer-Token als Secret, gibt es
  // dafuer trotzdem einen Satz statt des generischen Fallbacks.
  ADS_DEVELOPER_TOKEN_MISSING: 'Der Ads-Developer-Token fehlt als Secret. Das ist ein Fehler der Technik.',
};

/** Deutscher Satz zu einem Fehlercode, mit Fallback auf den rohen Code. */
export function structureErrorText(code: string): string {
  return STRUCTURE_ERROR_TEXT[code] ?? `Die Function hat abgelehnt: ${code}`;
}

// ═════════════════════════════════════════════════════════════════════════════
// Hooks
// ═════════════════════════════════════════════════════════════════════════════

export const ADS_STRUCTURE_KEY = 'ads-structure';

const structureOverviewKey = (userId: string | undefined, customerId: string | null) =>
  [ADS_STRUCTURE_KEY, 'overview', userId ?? null, customerId];

/** `action: "structure_overview"` -- alle Kampagnen des Kontos, mit Registerstand. */
export function useStructureOverview(customerId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: structureOverviewKey(user?.id, customerId),
    enabled: !!user?.id && !!customerId,
    staleTime: 60_000,
    retry: adsQueryRetry,
    queryFn: async (): Promise<StructureOverview> =>
      normalizeStructureOverview(
        await invokeAdsStructureRun<unknown>({ action: 'structure_overview', ads_customer_id: customerId }),
      ),
  });
}

export interface StructureWriteVariables {
  action: 'set_budget' | 'set_troas' | 'campaign_pause';
  ads_customer_id: string;
  campaign_resource_name: string;
  /** `set_budget`, Micros als String. */
  new_budget_micros?: string;
  /** `set_troas`, Verhaeltnis (z.B. 5.5 fuer 5,5x). */
  new_target_roas?: number;
  dry_run: boolean;
  /** Pflicht im Messfenster fuer den REALEN Lauf, optional in der Vorschau. */
  reason?: string;
  /** TOCTOU-Pin des realen Laufs -- der in der Vorschau gesehene Ist-Wert. */
  expected_budget_micros?: string;
  /** `null` ist ein gueltiger Pin: "es war noch kein Ziel gesetzt" (Erstsetzung). */
  expected_target_roas?: number | null;
  expected_status?: string;
}

/**
 * Fehlercodes, unter denen ein FEHLGESCHLAGENER realer Lauf die Uebersicht
 * trotzdem veraltet hinterlaesst (I2, Fix-Welle nach dem Task-Review):
 *
 *   - `VERIFY_FAILED` (502): der Mutate ist bei Google angekommen -- der
 *     Handler schreibt das Aenderungs-Log AUCH bei gescheitertem Read-back
 *     (`handlers.ts:917-919,945-953`, Kommentar dort: "der Mutate ist bei
 *     Google angekommen, und der Cooldown muss ihn zaehlen"). Ohne
 *     Invalidierung zeigt die Uebersicht weiter den alten Wert, obwohl
 *     Cooldown und (bei Budget) die Strukturbudget-Summe sich bereits bewegt
 *     haben koennen.
 *   - `STALE_PREVIEW` (409, nur im realen Lauf): der frisch gelesene Ist-Wert
 *     weicht vom in der Vorschau gesehenen ab -- die Uebersicht traegt selbst
 *     noch den ALTEN Wert und muss neu geladen werden, damit der Dialog beim
 *     naechsten "Aktuell ..." den frischen Stand zeigt.
 *
 * Beide Codes koennen strukturell nur bei `dry_run: false` auftreten (die
 * Function prueft `!dryRun` vor beiden Faellen, `handlers.ts:726,772`) --
 * eine Vorschau invalidiert hier also nie versehentlich mit.
 */
const OVERVIEW_STALE_ON_ERROR = new Set(['VERIFY_FAILED', 'STALE_PREVIEW']);

/**
 * Eine Zustellstelle fuer alle drei Hebel (Budget, Wertziel, Pause) und beide
 * Laufarten (Vorschau, realer Lauf) -- die Function unterscheidet selbst ueber
 * `body.action` und `body.dry_run`.
 *
 * Vorschau lebt bewusst NICHT in `onSuccess`/im Query-Cache (Spec Paragraph
 * 10.2, harte Grenze wie `shownRun` beim Tier-Uebernehmen): die aufrufende
 * Komponente haelt das Vorschau-Ergebnis in ihrem eigenen `useState`. Nur der
 * REALE Lauf (`dry_run === false`) invalidiert die Uebersicht und das
 * Aenderungs-Log -- eine Vorschau aendert nichts.
 */
export function useStructureWrite() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<StructureWriteResponse, unknown, StructureWriteVariables>({
    mutationFn: async (variables): Promise<StructureWriteResponse> => {
      const { action, ...body } = variables;
      return normalizeWriteResponse(await invokeAdsStructureRun<unknown>({ ...body, action }));
    },
    onSuccess: (data, variables) => {
      if (data.dry_run === false) {
        queryClient.invalidateQueries({
          queryKey: structureOverviewKey(user?.id, variables.ads_customer_id),
        });
        // Praefix-Treffer, Muster `useAdsSteuerung.ts`: der reale Lauf schreibt
        // sein eigenes Log ueber `ads-structure-run` selbst, geteilte Karte.
        queryClient.invalidateQueries({ queryKey: [ADS_STEUERUNG_KEY, 'change-log'] });
      }
    },
    onError: (error, variables) => {
      const code = adsErrorCode(error);
      if (code === null || !OVERVIEW_STALE_ON_ERROR.has(code)) return;
      queryClient.invalidateQueries({
        queryKey: structureOverviewKey(user?.id, variables.ads_customer_id),
      });
      // Nur VERIFY_FAILED hat das Log tatsaechlich geschrieben (siehe oben);
      // STALE_PREVIEW ist ein reiner Lesekonflikt vor jedem Google-Aufruf und
      // schreibt nichts (`handlers.ts:772-774`).
      if (code === 'VERIFY_FAILED') {
        queryClient.invalidateQueries({ queryKey: [ADS_STEUERUNG_KEY, 'change-log'] });
      }
    },
  });
}

export { isAdsSteuerungError, adsErrorCode };
export { AdsSteuerungError };
